import { faCheck, faMinus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColumnProps } from 'antd/lib/table';
import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useToggle } from 'react-use';
import { Button, Modal, Skeleton, Table, useMe, UserName } from 'src/common';
import { useAddCompetitors, useRemoveCompetitors } from 'src/models/company/Competitors/hooks';
import SsoConfigInfo from './SsoConfigInfo';
import { PricePlanSelect } from '../RoleSelect/PricePlanSelect';
import { FeatureRoleSelect } from '../RoleSelect/FeatureRoleSelect';
import { DeactivateCompany } from '../RoleSelect/DeactivateCompany';
import EmployeeStatus, { EmployeeStatusBadge } from 'src/pages/SettingsPage/Company/EmployeesSettings/EmployeeStatus';
import AdminCompaniesFilter from './AdminCompaniesFilter';
import NutsCodesTree from 'src/shared/MatchingProfile/NutCodesTree';
import { useAdminCompany } from 'src/models/admin/CompanyUsers/hooks';
import tableStyles from '../UserList/index.module.scss';
import styles from './index.module.scss';
import InviteUserButton from './InviteUserButton';
import UsersSelect from './UserSelect';
import AddUserModal from '../AddUserModal';
import DeleteUser from '../UserList/DeleteUser';
import { FeatureFlag, useFeatureFlag } from 'src/helpers/featureFlag';
import { MfaMode } from 'src/models/users/types';
import { ICompanyUser } from 'src/models/company/Users/types';
import { Ownerships, ownership } from 'src/models/ownership';
import { SalesOfficeSelect } from '../RoleSelect/SalesOfficeSelect';

export const CompanyMembersList: FC = () => {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId?: string }>();
  const isDeleteUserFeature = useFeatureFlag(FeatureFlag.Users_DeleteUser);
  const isInviteFeature = ownership.name !== Ownerships.Clira;
  const { loading, data: company } = useAdminCompany(companyId);
  const { data: meData } = useMe();

  const columns: ColumnProps<ICompanyUser>[] = useMemo(
    () => [
      {
        title: t('Common.name'),
        dataIndex: 'name',
        fixed: 'left',
        ellipsis: true,
        key: 'user.name',
        className: tableStyles.colName,
        render: (_, record) => {
          return <UserName user={record} className={styles.userName} showTooltip size={'s'} />;
        }
      },
      {
        title: t('Common.email'),
        dataIndex: 'email',
        fixed: 'left',
        ellipsis: true,
        className: tableStyles.colEmail,
        key: 'user.email',
        render: (_, record) => {
          return <span>{record.email}</span>;
        }
      },

      {
        title: t('Common.createdOn'),
        dataIndex: 'createdOn',
        className: tableStyles.colDate,
        key: 'user.createdOn'
      },
      {
        title: t('AdminPage.mainAccount'),
        dataIndex: 'isAdmin',
        className: tableStyles.colIsAdmin,
        key: 'user.isAdmin',
        render: isAdmin => <FontAwesomeIcon icon={isAdmin ? faCheck : faMinus} />
      },
      {
        title: '2FA',
        dataIndex: 'mfaMode',
        className: tableStyles.colIsAdmin,
        align: 'center',
        key: 'user.mfaMode',
        render: mfaMode => (mfaMode === MfaMode.TOTP ? t('Common.activated') : <FontAwesomeIcon icon={faMinus} />)
      },
      {
        title: t('Common.status'),
        dataIndex: 'status',
        className: tableStyles.colIsAdmin,
        render: function RenderActiveStatus(_, record) {
          const { enabled, email } = record;
          return (
            <>
              {meData?.email === email ? (
                <EmployeeStatusBadge enabled={enabled} />
              ) : (
                <EmployeeStatus top enabled={enabled} email={email} />
              )}
            </>
          );
        }
      },
      {
        title: '',
        className: tableStyles.colAction,
        fixed: 'right',
        render: (_, record) => {
          // The invite button field in the admin panel should not show for SSO users but should show for rest
          // TODO: SSO User fix and confirm any other cases but this is not prioritized
          return (
            <>
              {isInviteFeature && <InviteUserButton user={record} />}
              {isDeleteUserFeature && <DeleteUser email={record.email} />}
            </>
          );
        }
      }
    ],
    [isDeleteUserFeature, isInviteFeature, meData?.email, t]
  );

  const data = company?.users || [];

  const [editCompetitorsVisible, toggleEditCompetitorsVisible] = useToggle(false);
  const onToggleClick = useCallback(() => {
    toggleEditCompetitorsVisible();
  }, [toggleEditCompetitorsVisible]);

  const [companyNutsFilterVisible, toggleCompanyNutsFilterVisible] = useToggle(false);
  const onCompanyNutsFilterToggleClick = useCallback(() => {
    toggleCompanyNutsFilterVisible();
  }, [toggleCompanyNutsFilterVisible]);

  const [createUserVisible, toggleCreateUserVisible] = useToggle(false);
  const onCreateUserVisibleToggleClick = useCallback(() => {
    toggleCreateUserVisible();
  }, [toggleCreateUserVisible]);

  const [addCompetitors, { loading: addCompetitorsLoading }] = useAddCompetitors();
  const [removeCompetitors] = useRemoveCompetitors();
  const competitors = useMemo(() => company?.competitors || [], [company?.competitors]);
  const orgNumbers = useMemo(() => {
    return competitors.map(competitor => competitor.orgId) || [];
  }, [competitors]);
  const ssoConfig = company?.ssoConfig;

  return (
    <>
      <div className={tableStyles.container}>
        <div className={tableStyles.overviewCard}>
          {company && (
            <div className={styles.header}>
              <div className={styles.headerUnits}>
                <div className={styles.headerUnit}>
                  <div className={styles.infoLabel}>{`${t('Company.company')}:`}</div>
                  <span className={styles.infoValue}>{company.companyName}</span>
                </div>
                <div className={styles.headerUnit}>
                  <div className={styles.infoLabel}>{`${t('Company.orgNumber')}:`}</div>
                  <span className={styles.infoValue}>{company.orgNumber}</span>
                </div>
              </div>
              <div className={styles.headerUnits}>
                <div className={styles.headerUnit}>
                  <div className={styles.infoLabel}>{`${t('Common.createdOn')}:`}</div>
                  <span className={styles.infoValue}>{company.createdOn}</span>
                </div>
                <div className={styles.headerUnit}>
                  <div className={styles.infoLabel}>{`${t('AdminPage.admin')}:`}</div>
                  <UsersSelect
                    companyId={company.id}
                    companyName={company.companyName}
                    selected={company.createdBy ?? null}
                  />
                </div>
              </div>
              <div className={styles.headerUnits}>
                <div className={styles.headerUnit}>
                  <div className={styles.infoLabel}>{`${t('AdminPage.pricePlan')}:`}</div>
                  <PricePlanSelect companyId={company.id} pricePlan={company.pricePlan} />
                </div>
                <div className={styles.headerUnit}>
                  <div className={styles.infoLabel}>{`${t('AdminPage.featureRole')}:`}</div>
                  <FeatureRoleSelect companyId={company.id} featureRoles={company.featureRoles} />
                </div>
                <div className={styles.headerUnit}>
                  <div className={styles.infoLabel}>{`${t('AdminPage.notActivatedRole')}:`}</div>
                  <DeactivateCompany companyId={company.id} enabled={company.enabled} />
                </div>
                <div className={styles.headerUnit}>
                  <div className={styles.infoLabel}>{`${t('AdminPage.salesOffice')}:`}</div>
                  <SalesOfficeSelect companyId={company.id} country={company.country} />
                </div>
              </div>
              <div className={styles.headerUnits}>
                <div className={styles.headerUnit}>
                  <Button type="primary" onClick={onToggleClick}>
                    {t('AdminPage.editCompetitors')}
                  </Button>
                </div>
                <div className={styles.headerUnit}>
                  <Button type="primary" onClick={onCompanyNutsFilterToggleClick}>
                    {t('AdminPage.editCompanyNutsFilter')}
                  </Button>
                </div>
                <div className={styles.headerUnit}>
                  <Button type={'primary'} onClick={onCreateUserVisibleToggleClick}>
                    {t('AdminPage.createUser')}
                  </Button>
                </div>
                {ssoConfig && (
                  <div className={styles.headerUnit}>
                    <SsoConfigInfo ssoConfig={ssoConfig} />
                  </div>
                )}
              </div>
            </div>
          )}
          {loading ? (
            <Skeleton loading active />
          ) : (
            <Table
              pagination={false}
              rowKey={'email'}
              columns={columns}
              dataSource={data}
              scroll={{ x: 1500 }}
              rowClassName={tableStyles.row}
              className={tableStyles.table}
            />
          )}
        </div>
      </div>
      {company && (
        <Modal
          width={690}
          title={t('AdminPage.editCompetitors')}
          visible={editCompetitorsVisible}
          onCancel={onToggleClick}
          footer={null}
        >
          <AdminCompaniesFilter
            loading={loading}
            addOrgsLoading={addCompetitorsLoading}
            orgNumbers={orgNumbers}
            addOrgs={selectedIds => addCompetitors(company.id, selectedIds)}
            removeOrgs={(selectedId, updatingIds, onComplete) => removeCompetitors(company.id, selectedId, onComplete)}
          />
        </Modal>
      )}
      {company && (
        <Modal
          width={690}
          title={t('AdminPage.editCompanyNutsFilter')}
          visible={companyNutsFilterVisible}
          onCancel={onCompanyNutsFilterToggleClick}
          footer={null}
        >
          <NutsCodesTree disabled={!companyNutsFilterVisible} companyNutsCodeView={true} companyId={company.id} />
        </Modal>
      )}
      {companyId && createUserVisible && <AddUserModal companyId={companyId} onHide={onCreateUserVisibleToggleClick} />}
    </>
  );
};

export default CompanyMembersList;
